import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Fragment, useCallback, useEffect, useState } from "react";
import LabelWrapper from "../../root/LabelWrapper";

import TimePicker from "react-time-picker";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { deDE } from "@mui/x-date-pickers/locales";
import ManyToOneSelect from "@/shared/forms/ManyToOneSelect";
import CalculatorTextField from "@/components/planning/dialogs/CalculatorTextField";
import { SupplierOrder } from "@/components/planning/Project";
import ExternalMaterialSelector from "./ExternalMaterialSelector";

export interface NewExternalOrder {
  id: number;
  comment: string;
  article: {
    id: number;
    number: string;
    name: string;
    unit: string;
    productGroup: string;
    productSubGroup: string;
  };
  position: {
    id: number;
    supplierOrder: {
      id: number;
      name: string;
      number: string;
      validFrom: string;
      projectNumber: string;
      validUntil: string;
    };
    deliveryType: string;
  };
  contact: string;
  amount: number;
  project: string;
  logistics: string;
  date: string;
  plant: {
    id: number;
    name: string;
    number: string;
  };
  firstLoad: string;
  number: string;
  customer: {
    id: number;
    number: string;
    name: string;
    email: string;
    street: string;
    city: string;
    phone: string;
  };
}

export default function AddNewExternalDialog({
  save,
  OpenButton,
  initialState,
  allowedPlant,
}: {
  save: (order: NewExternalOrder) => void;
  OpenButton: any;
  initialState: NewExternalOrder;
  allowedPlant: number;
}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<NewExternalOrder>(initialState);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const openAndSet = useCallback(() => {
    console.log("OPEN EDITOR");
    setState(initialState);
    setOpen(true);
  }, [initialState]);

  const valid = !!state.plant && state.amount > 0 && !!state.customer && !!state.article;

  return (
    <LocalizationProvider adapterLocale="de" dateAdapter={AdapterDayjs} localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}>
      <OpenButton onClick={openAndSet} />
      {open ? (
        <Dialog open={open} maxWidth="xl" fullWidth onClose={close}>
          <Fragment>
            <DialogTitle
              sx={{
                p: 1,
                px: 2,
                background: "#002196",
                color: "white",
                borderBottom: "1px solid rgba(255,255,255,0.5)",
              }}
            >
              {state.id === 0 ? "Neuer externer Auftrag" : "Externen Auftrag bearbeiten"}
            </DialogTitle>
            <DialogContent
              sx={{
                p: 0,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <LabelWrapper label="Datum">
                <DatePicker
                  {...{
                    slotProps: { textField: { size: "small" } },
                    value: dayjs(state.date, "YYYY-MM-DD"),
                    onChange: (val) => setState((s) => ({ ...s, date: val.format("YYYY-MM-DD") })),
                    displayWeekNumber: true,
                  }}
                />
              </LabelWrapper>
              <ManyToOneSelect<NewExternalOrder["plant"]>
                entity="plant"
                showWithoutChoice
                version="external"
                value={state.plant}
                placeholder="Bitte wählen..."
                size="small"
                onChange={(plant: NewExternalOrder["plant"] | null) =>
                  setState((s) => ({
                    ...s,
                    plant,
                    article: null,
                    position: null,
                  }))
                }
                wrapper={(c) => <LabelWrapper label="Mischanlage">{c}</LabelWrapper>}
                remoteFilter={{ deletedAt: null, supplier: ["supplier", [2, 6]], ...(allowedPlant > 0 ? { id: allowedPlant } : {}) }}
              />
              <ManyToOneSelect<NewExternalOrder["customer"]>
                entity="externalMaterialCustomer"
                showWithoutChoice
                version="external"
                placeholder="Bitte wählen..."
                value={state.customer}
                showAllOptions={false}
                size="small"
                onChange={(customer: NewExternalOrder["customer"] | null) =>
                  setState((s) => ({
                    ...s,
                    customer,
                    article: null,
                    position: null,
                  }))
                }
                wrapper={(c) => <LabelWrapper label="Kunde">{c}</LabelWrapper>}
                remoteFilter={{ deletedAt: null }}
              />
              <ExternalMaterialSelector
                plantId={state.plant?.id ?? null}
                customerId={state.customer?.id ?? null}
                article={state.article ?? null}
                position={state.position ?? null}
                date={state.date}
                onChange={(article, position) =>
                  setState((s) => ({
                    ...s,
                    article,
                    position,
                    project: position?.supplierOrder.name || s.project,
                    number: position?.supplierOrder.projectNumber || s.number,
                  }))
                }
              />

              <LabelWrapper label="Erster LKW an Waage">
                <ToggleButtonGroup
                  size="small"
                  orientation="horizontal"
                  exclusive
                  value={!state.firstLoad ? "none" : state.firstLoad.startsWith("x") ? state.firstLoad : "custom"}
                  onChange={(_, fu) =>
                    fu &&
                    setState((s) => ({
                      ...s,
                      firstLoad: fu === "none" ? null : fu.startsWith("x") ? fu : "07:00",
                    }))
                  }
                >
                  <ToggleButton value="none">anschließend</ToggleButton>
                  <ToggleButton value="xAM">vormittags</ToggleButton>
                  <ToggleButton value="xPM">nachmittags</ToggleButton>
                  <ToggleButton value="custom">feste Uhrzeit</ToggleButton>
                </ToggleButtonGroup>
                {!!state.firstLoad && !state.firstLoad.startsWith("x") ? (
                  <div style={{ marginTop: "5px" }}>
                    <TimePicker
                      onChange={(firstLoad) =>
                        setState((s) => ({
                          ...s,
                          firstLoad,
                        }))
                      }
                      className={!state.firstLoad ? "nostart" : ""}
                      value={state.firstLoad}
                    />
                  </div>
                ) : null}
              </LabelWrapper>
              <LabelWrapper label={"Menge" + (!!state.article ? " in " + state.article.unit : "")}>
                <CalculatorTextField
                  value={state.amount}
                  setValue={(amount) =>
                    setState((s) => ({
                      ...s,
                      amount,
                    }))
                  }
                />
              </LabelWrapper>

              <LabelWrapper label="Kostenstelle">
                <TextField
                  size="small"
                  fullWidth
                  value={state.number}
                  placeholder="Kostenstelle"
                  multiline
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      number: (e.target as HTMLInputElement).value,
                    }))
                  }
                />
              </LabelWrapper>
              <LabelWrapper label="Baustelle">
                <TextField
                  size="small"
                  fullWidth
                  value={state.project}
                  placeholder="Kunde/Baustelle"
                  multiline
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      project: (e.target as HTMLInputElement).value,
                    }))
                  }
                />
              </LabelWrapper>
              <LabelWrapper label="Ansprechpartner">
                <TextField
                  size="small"
                  fullWidth
                  value={state.contact}
                  placeholder="Ansprechpartner"
                  multiline
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      contact: (e.target as HTMLInputElement).value,
                    }))
                  }
                />
              </LabelWrapper>
              <LabelWrapper label="Weitere Informationen">
                <TextField
                  size="small"
                  fullWidth
                  value={state.comment}
                  placeholder="Weitere Informationen"
                  multiline
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      comment: (e.target as HTMLInputElement).value,
                    }))
                  }
                />
              </LabelWrapper>
            </DialogContent>
            <DialogActions sx={{ borderTop: "1px solid #aaa" }}>
              <Button onClick={close}>Abbrechen</Button>
              <Button
                variant="outlined"
                color="primary"
                disabled={!valid}
                onClick={() => {
                  save(state);
                  close();
                }}
              >
                OK
              </Button>
            </DialogActions>
          </Fragment>
        </Dialog>
      ) : null}
    </LocalizationProvider>
  );
}
